import { useTranslation } from 'react-i18next';
import StepProgress from '@features/user/checkout/layout/StepProgress';
import { Button } from '@features/ui';
import { TradingPlatForms, TTradingPlatform } from '@src/types';
import { useEffect, useState } from 'react';
import { useCheckoutStore } from '@src/stores/checkoutStore';
import { useNavigate } from 'react-router-dom';
import { getAllTradingPlatforms } from '@src/services/tradingPlatforms';

import CheckIcon from '../../../../components/CheckIcon';

export const SelectPlatformPageV2 = () => {
  const { platform, setPlatform, setAccount } = useCheckoutStore();
  const navigate = useNavigate();

  const [selectedPlatform, setSelectedPlatform] = useState(
    platform ?? ""
  );
  const [platformsData, setPlatformsData] = useState<{
    // ninjatrader?: TTradingPlatform;
    rithmic?: TTradingPlatform;
  }>({});
  const { t } = useTranslation();

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const response = await getAllTradingPlatforms();
        const platforms = response.data;

        // const ninjatrader = platforms.find(
        //   (p) => p.type === TradingPlatForms.CQG_DATA_FEED && p.is_publish
        // );
        const rithmic = platforms.find(
          (p) => p.type === TradingPlatForms.RITHMIC_DATA_FEED && p.is_publish
        );

        setPlatformsData({ /* ninjatrader, */ rithmic });
      } catch (error) {
        console.error('Failed to fetch trading platforms:', error);
      }
    };

    fetchPlatforms();
  }, []);

  const getArray = (key: string) => {
    const arr = t(key, { returnObjects: true });

    return Array.isArray(arr) ? arr : [];
  };

  const renderPlatformContent = (content?: string) => {
    if (!content) return null;

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const elements: React.ReactElement[] = [];

    doc.body.childNodes.forEach((node, index) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;

        if (element.tagName === 'P') {
          elements.push(
            <div
              key={`p-${index}`}
              className="text-[#CECECE] font-medium text-[14px]"
            >
              {element.textContent}
            </div>
          );
        } else if (element.tagName === 'UL') {
          const listItems: React.ReactElement[] = [];

          element.querySelectorAll('li').forEach((li, liIndex) => {
            listItems.push(
              <li key={`li-${liIndex}`} className="flex items-center gap-2">
                <CheckIcon />
                <span className="text-[#CECECE]">{li.textContent}</span>
              </li>
            );
          });
          elements.push(
            <ul
              key={`ul-${index}`}
              className="text-[#B1B5C3] text-sm my-[49px]"
            >
              {listItems}
            </ul>
          );
        }
      }
    });

    return elements;
  };

  const onContinue = () => {
    setPlatform(selectedPlatform);
    setAccount(null);
    navigate('/select-trading-account-type');
  };

  return (
    <>
      <div
        className="max-w-[1440px] m-auto px-6 mt-4 py-4 lg:px-[60px]"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)' /* 5% = 0.05 opacity */,
          backdropFilter: 'blur(200px)',
        }}
      >
        <div className="mb-8">
          <StepProgress current={1} platform={selectedPlatform} total={5} />
        </div>
        <h2 className="font-bold text-[23px] leading-[100%] tracking-normal text-white mb-2">
          {t('selectPlatformPage.title')}
        </h2>
        <p className="pt-[10px] font-medium text-[16px] leading-[100%] tracking-normal text-white mb-[36px]">
          {t('selectPlatformPage.desc')}
        </p>
        <div className="w-full flex gap-[28px] mb-12">
          {/* Card: NinjaTrader Tradovate (POPULAR) - COMMENTED OUT */}
          {/*<div
            className={`w-full max-w-[342px] bg-[#25334880] rounded-[10px] border pt-[107px] pb-[29px] px-[20px] flex flex-col justify-between min-h-[411px] relative ${selectedPlatform === TradingPlatForms.CQG_DATA_FEED ? 'border-[#007AFE] border-[2px]' : 'border-transparent'}`}
          >
            <div className="flex items-center justify-center mb-[35px]">
              <div className="flex items-center justify-center gap-[27px]">
                <div>
                  <img
                    alt="NinjaTrader"
                    className="w-[73px] h-[73px] object-contain"
                    src="/assets/logo-ninjatrader.png"
                  />
                  <div className={'text-white font-bold text-[18px] mt-[6px]'}>
                    NinjaTrader
                  </div>
                </div>
              </div>
            </div>
            {platformsData.ninjatrader?.content ? (
              renderPlatformContent(platformsData.ninjatrader.content)
            ) : (
              <>
                <div className="text-[#CECECE] font-medium text-[14px]">
                  {t('selectPlatformPage.card.ninjaTraderTradovate.desc')}
                </div>
                <ul className="text-[#B1B5C3] text-sm my-[49px]">
                  {getArray(
                    'selectPlatformPage.card.ninjaTraderTradovate.features'
                  ).map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckIcon />
                      <span className="text-[#CECECE]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <Button
              className="w-full font-bold text-[16px]"
              size="lg"
              variant={
                selectedPlatform === TradingPlatForms.CQG_DATA_FEED
                  ? 'gradient'
                  : 'outline'
              }
              onClick={() =>
                setSelectedPlatform(TradingPlatForms.CQG_DATA_FEED)
              }
            >
              {t('selectPlatformPage.card.ninjaTraderTradovate.selectBtn')}
            </Button>
            <span className="absolute right-[30px] -top-[1px] w-[171px] h-[58px] bg-[#DBE7F5] text-[#DC342D] text-base font-bold flex items-center justify-center rounded-br-[10px] rounded-bl-[10px] shadow-md">
              {t('selectPlatformPage.card.ninjaTraderTradovate.popularBadge')}
            </span>
          </div>*/}

          {/* Card: Rithmic */}
          <div
            className={`w-full max-w-[342px] bg-[#25334880] rounded-[10px] border pt-[107px] pb-[31px] px-[20px] flex flex-col 
            justify-between min-h-[411px] relative ${selectedPlatform === TradingPlatForms.RITHMIC_DATA_FEED ? 'border-[#CA4742]' : 'border-transparent'}`}
          >
            <div className="flex items-center justify-between mb-[40px]">
              <img
                alt="Rithmic"
                className="w-[128px] h-[98px] object-contain"
                src="/assets/rithmic.png"
              />
            </div>
            {platformsData.rithmic?.content ? (
              renderPlatformContent(platformsData.rithmic.content)
            ) : (
              <>
                <div className="text-[#CECECE] font-medium text-[14px]">
                  {t('selectPlatformPage.card.rithmic.desc')}
                </div>
                <ul className="text-[#B1B5C3] text-sm mb-4">
                  {getArray('selectPlatformPage.card.rithmic.features').map(
                    (feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckIcon />
                        <span>{feature}</span>
                      </li>
                    )
                  )}
                </ul>
              </>
            )}
            <Button
              className="w-full font-bold text-[16px] border-[#ffffff]"
              size="lg"
              variant={
                selectedPlatform === TradingPlatForms.RITHMIC_DATA_FEED
                  ? 'gradient'
                  : 'outline'
              }
              onClick={() =>
                setSelectedPlatform(TradingPlatForms.RITHMIC_DATA_FEED)
              }
            >
              {t('selectPlatformPage.card.rithmic.selectBtn')}
            </Button>
            <span className="absolute right-[31px] -top-[1px] px-[20px] h-[58px] bg-[#DBE7F5] text-[#DC342D] text-base font-bold flex items-center justify-center rounded-br-[10px] rounded-bl-[10px] shadow-md">
              {t('selectPlatformPage.card.rithmic.competitionBadge')}
            </span>
          </div>

          {/* Card: DXFeed */}
          {/* <div
            className={`w-full max-w-[342px] bg-[#25334880] rounded-[10px] border pt-[29px] pb-[29px] px-[20px] flex flex-col justify-between min-h-[411px] ${selectedPlatform === TradingPlatForms.DX_DATA_FEED ? 'border-[#CA4742]' : 'border-transparent'}`}
          >
            <div className="flex gap-[6px] flex-wrap items-center">
              <div
                className={
                  'bg-[#262A30] min-w-[127px] py-[17px] pl-[13px] pr-4 rounded-[6px]'
                }
              >
                <img
                  alt="DXFeed"
                  className=" w-full max-w-[99px] object-contain"
                  src="/assets/image-1-step1.png"
                />
              </div>
              <div
                className={
                  'bg-[#262A30] w-[127px] py-[14px] px-[27px] rounded-[6px]'
                }
              >
                <img
                  alt="DXFeed"
                  className=" w-full max-w-[99px] object-contain"
                  src="/assets/image-2-step1.png"
                />
              </div>
              <div
                className={
                  'bg-[#262A30] w-[127px] py-[17px] pl-[13px] pr-4 rounded-[6px]'
                }
              >
                <img
                  alt="DXFeed"
                  className=" w-full max-w-[99px] object-contain"
                  src="/assets/image-1-step1.png"
                />
              </div>
            </div>
            <div className={'text-white font-bold text-[18px] -mt-[15px]'}>
              {t('selectPlatformPage.card.dxFeed.title')}
            </div>
            <div className="text-[#CECECE] font-medium text-[14px]">
              {t('selectPlatformPage.card.dxFeed.desc')}
            </div>
            <ul className="text-[#B1B5C3] text-sm mb-4 list-disc list-inside">
              {getArray('selectPlatformPage.card.dxFeed.features').map(
                (feature: string, idx: number) => (
                  <li key={idx} className="flex items-center gap-2">
                    <CheckIcon />
                    <span>{feature}</span>
                  </li>
                )
              )}
            </ul>
            <Button
              className="w-full font-bold text-[16px] border-[#ffffff]"
              size="lg"
              variant={
                selectedPlatform === TradingPlatForms.DX_DATA_FEED
                  ? 'gradient'
                  : 'outline'
              }
              onClick={() => setSelectedPlatform(TradingPlatForms.DX_DATA_FEED)}
            >
              {t('selectPlatformPage.card.dxFeed.selectBtn')}
            </Button>
          </div> */}
        </div>
      </div>

      <div className="w-full sticky bottom-0 backdrop-blur-[30px] bg-[linear-gradient(90deg,rgba(23,29,35,0.8)_0%,rgba(23,29,35,0.8)_100%)] border-t border-[#255589] h-[100px]">
        <div className="max-w-[1440px] m-auto px-6 lg:px-[60px] flex justify-end items-center h-full">
          <Button
            className="px-8"
            size="lg"
            variant="gradient"
            onClick={onContinue}
          >
            {t('selectPlatformPage.continueBtn')}
          </Button>
        </div>
      </div>
    </>
  );
};
